import { useState } from 'react';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  Text,
  Textarea,
  VStack,
  useColorModeValue,
  useToast,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { FiArrowLeft } from 'react-icons/fi';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
import { useMySupportTickets, createSupportTicket } from '../../hooks/useSupportTickets';

export default function CreateSupportTicket() {
  const { currentUser } = useAuth();
  const { userData } = useUser();
  const { tickets } = useMySupportTickets(currentUser?.uid);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();

  const [subject, setSubject] = useState(searchParams.get('subject') || '');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  const openCount = tickets.filter((t) => t.status === 'open').length;
  const canCreate = openCount < 2;
  const isValid = subject.trim().length >= 3 && message.trim().length >= 10;

  const handleSubmit = async () => {
    if (!currentUser || !userData || !isValid) return;
    setSubmitting(true);

    try {
      const ticketId = await createSupportTicket({
        subject: subject.trim(),
        message: message.trim(),
        uid: currentUser.uid,
        username: userData.anonymizedUsername || 'User',
      });

      toast({
        title: 'Ticket created',
        description: 'We\'ll get back to you as soon as possible.',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });

      navigate(`/support/${ticketId}`);
    } catch (err) {
      toast({
        title: 'Error creating ticket',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxW="2xl" py={6}>
      <VStack spacing={6} align="stretch">
        <Button
          as={Link}
          to="/support"
          variant="ghost"
          leftIcon={<Icon as={FiArrowLeft} />}
          alignSelf="flex-start"
          size="sm"
        >
          Back to Support
        </Button>

        <Box>
          <Heading size="lg">New Support Ticket</Heading>
          <Text color="gray.600" mt={1}>
            Describe your issue and we'll get back to you.
          </Text>
        </Box>

        {!canCreate && (
          <Alert status="warning" rounded="md">
            <AlertIcon />
            You already have 2 open tickets. Please wait for a response or close an existing ticket before creating a new one.
          </Alert>
        )}

        <Box bg={cardBg} rounded="xl" borderWidth={1} borderColor={borderColor} p={6}>
          <VStack spacing={5} align="stretch">
            <FormControl isRequired>
              <FormLabel>Subject</FormLabel>
              <Input
                placeholder="Brief summary of your issue"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                isDisabled={!canCreate}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Message</FormLabel>
              <Textarea
                placeholder="Describe your issue in detail..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={6}
                isDisabled={!canCreate}
              />
            </FormControl>

            <Button
              colorScheme="orange"
              onClick={handleSubmit}
              isLoading={submitting}
              loadingText="Submitting..."
              isDisabled={!isValid || !canCreate}
              alignSelf="flex-end"
            >
              Submit Ticket
            </Button>
          </VStack>
        </Box>
      </VStack>
    </Container>
  );
}
